import { useState, useEffect, useRef } from 'react';
import { useWorkspaceState } from '@/hooks/useWorkspaceState';
import { Search, Filter, Grid3x3, List, FileText, Calendar, Download, Eye, MoreVertical, ChevronLeft, ChevronRight, ChevronDown, Plus, Trash2, ExternalLink, Loader2, X, CheckCircle2, Bookmark, BookmarkCheck, FolderPlus, MessageSquarePlus, Upload, ScrollText, Globe, BookOpen, Scale, BookMarked } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import Markdown from 'markdown-to-jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { documentApi, collectionsApi, SessionDocument } from '@/services/api';
import { CollectionsManagerModal } from './CollectionsManagerModal';
import { API_CONFIG } from '@/config/api.config';
import { useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { useCompactMode } from '@/hooks/useCompactMode';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';
import { MultiDocQAPanel } from './MultiDocQAPanel';

interface Collection {
  collection_id: string;
  name: string;
  created_date: string;
  resource_ids: string[];
}

interface DocumentsPageProps {
  onDocumentSelect: (doc: any) => void;
  isDocumentViewerOpen?: boolean;
  onAddDocuments?: () => void;
}

export function DocumentsPage({ onDocumentSelect, isDocumentViewerOpen = false, onAddDocuments }: DocumentsPageProps) {
  // Fix adjacent bold/italic markers that confuse markdown parsers
  const fixMarkdownFormatting = (content: string): string => {
    if (!content) return content;

    // Replace **text***italic* pattern with proper HTML, preserving what comes after
    // Match: **bold***italic*[anything after]
    // This handles cases like **28***a*,**28***b*and
    let fixed = content.replace(/\*\*([^*]+?)\*\*\*([^*]+?)\*([,;:.\s])?/g, (match, bold, italic, after) => {
      // If there's punctuation or space after, preserve it; otherwise add a space before letters
      const suffix = after || '';
      return `<b>${bold}</b><i>${italic}</i>${suffix}`;
    });

    // Handle cases where italic is followed immediately by a letter (like *b*and)
    fixed = fixed.replace(/<\/i>([a-zA-Z])/g, '</i> $1');

    // Also handle *italic***bold* pattern
    fixed = fixed.replace(/\*([^*]+?)\*\*\*([^*]+?)\*\*/g, '<i>$1</i><b>$2</b>');

    return fixed;
  };

  // Convert markdown to HTML (for use in previews)
  const markdownToHtml = (content: string): string => {
    if (!content) return content;

    // First fix problematic adjacent markers
    let html = fixMarkdownFormatting(content);

    // Convert remaining bold **text**
    html = html.replace(/\*\*([^*]+?)\*\*/g, '<b>$1</b>');

    // Convert remaining italic *text*
    html = html.replace(/\*([^*]+?)\*/g, '<i>$1</i>');

    return html;
  };

  // Format resource ID for display (e.g., "patent-9864572" -> "US Patent 9864572")
  const formatResourceId = (resourceId: string): string => {
    if (!resourceId) return '';

    // Handle patent and publication types
    if (resourceId.startsWith('patent-')) {
      const number = resourceId.replace('patent-', '');
      return `US Patent ${number}`;
    } else if (resourceId.startsWith('publication-')) {
      const number = resourceId.replace('publication-', '');
      return `US Publication ${number}`;
    } else if (resourceId.startsWith('foreign_patent-')) {
      // For foreign patents, just return the document number
      return resourceId.replace('foreign_patent-', '');
    }

    // For other types, just replace hyphen with space
    return resourceId.replace('-', ' ');
  };

  // Format date with time in local timezone (e.g., "11/20/2025 at 7:02 PM")
  const formatDateTime = (dateString: string): string => {
    if (!dateString) return 'Never';
    // Ensure the date string is treated as UTC
    // If it doesn't end with 'Z' or have a timezone offset, append 'Z'
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    const dateStr = date.toLocaleDateString('en-US'); // MM/DD/YYYY
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }); // h:MM AM/PM
    return `${dateStr} at ${timeStr}`;
  };

  // Format date only in local timezone (e.g., "11/20/2025")
  const formatDate = (dateString: string): string => {
    if (!dateString) return 'Unknown';
    // Ensure the date string is treated as UTC
    let normalizedDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      normalizedDateString = dateString + 'Z';
    }
    const date = new Date(normalizedDateString);
    return date.toLocaleDateString('en-US'); // MM/DD/YYYY
  };

  const [viewMode, setViewMode] = useWorkspaceState<'grid' | 'list'>('documents:view', 'list', value => value === 'grid' || value === 'list');
  const [searchQuery, setSearchQuery] = useWorkspaceState('documents:search', '');
  const [filterType, setFilterType] = useWorkspaceState('documents:type', 'all');
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sessionDocuments, setSessionDocuments] = useState<SessionDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<SessionDocument | null>(null);
  const [documentLimit, setDocumentLimit] = useState(50);
  const [hasMoreDocuments, setHasMoreDocuments] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isFullTextSearch, setIsFullTextSearch] = useWorkspaceState('documents:full-text', false);
  const [semanticResults, setSemanticResults] = useState<any[]>([]);
  const [storageUpdateTrigger, setStorageUpdateTrigger] = useState(0);
  const [isSemanticSearching, setIsSemanticSearching] = useState(false);
  const [semanticSearchError, setSemanticSearchError] = useState('');
  const [hasSearchedOnce, setHasSearchedOnce] = useState(false);

  // Metadata search (fast search by filename, ID, title)
  const [metadataSearchResults, setMetadataSearchResults] = useState<SessionDocument[]>([]);
  const [isMetadataSearching, setIsMetadataSearching] = useState(false);
  const [metadataSearchError, setMetadataSearchError] = useState('');
  const [metadataSearchVersion, setMetadataSearchVersion] = useState(0);
  const semanticRequestRef = useRef(0);
  const searchIdentityRef = useRef('');
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();
  const { compactMode } = useCompactMode();

  // Collections state
  const [collections, setCollections] = useState<Collection[]>([]);
  const [selectedCollectionId, setSelectedCollectionId] = useWorkspaceState('documents:collection', 'all');
  const [showCollectionDialog, setShowCollectionDialog] = useState(false);
  const [collectionDialogDoc, setCollectionDialogDoc] = useState<SessionDocument | null>(null);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [showCollectionsManager, setShowCollectionsManager] = useState(false);
  const [collectionPlaceholders, setCollectionPlaceholders] = useState<SessionDocument[]>([]);
  const [showMultiDocQA, setShowMultiDocQA] = useState(false);
  searchIdentityRef.current = JSON.stringify([searchQuery.trim(), isFullTextSearch, selectedCollectionId]);

  useEffect(() => {
    semanticRequestRef.current += 1;
    setIsSemanticSearching(false);
    setSemanticResults([]);
    setSemanticSearchError('');
    setHasSearchedOnce(false);
    return () => { semanticRequestRef.current += 1; };
  }, [searchQuery, isFullTextSearch, selectedCollectionId]);

  // Debounced metadata search (when NOT in full text mode)
  useEffect(() => {
    let active = true;
    setMetadataSearchResults([]);
    setMetadataSearchError('');
    setIsMetadataSearching(!isFullTextSearch && searchQuery.trim().length >= 2);
    const searchDocuments = async () => {
      // Only search if NOT in full text mode
      if (isFullTextSearch) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      if (!searchQuery.trim()) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      if (searchQuery.trim().length < 2) {
        setMetadataSearchResults([]);
        setIsMetadataSearching(false);
        return;
      }

      console.log('[DocumentsPage] Metadata search for:', searchQuery);
      setIsMetadataSearching(true);
      try {
        const results = await documentApi.searchDocuments(searchQuery, 100, undefined, { throwOnError: true });
        if (!active) return;
        console.log('[DocumentsPage] Metadata search results:', results);
        setMetadataSearchResults(results);
      } catch (error) {
        if (!active) return;
        console.error('[DocumentsPage] Metadata search failed:', error);
        setMetadataSearchResults([]);
        setMetadataSearchError('Search failed. Your query is kept; select Search to retry.');
      } finally {
        if (active) setIsMetadataSearching(false);
      }
    };

    const timeoutId = setTimeout(searchDocuments, 350);
    return () => {
      active = false;
      clearTimeout(timeoutId);
    };
  }, [searchQuery, isFullTextSearch, metadataSearchVersion]);

  useEffect(() => {
    loadSessionDocuments();

    // Listen for job completion events from the global polling store
    // Note: Notifications are handled globally in Main.tsx, this just handles cleanup
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      console.log('[DocumentsPage] Job completion event received:', event.detail);
      // Format type name properly (handle foreign_patent -> Foreign Patent)
      const resourceTypeName = docType === 'foreign_patent'
        ? 'Foreign Patent'
        : docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        removeLoadingDocument(docId, resourceTypeName);
        // Refresh the documents list to show the newly onboarded document
        loadSessionDocuments();
      } else {
        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'patent') {
          context.current_patents = (context.current_patents || []).filter((id: string) => id !== docId);
        } else if (docType === 'publication') {
          context.current_publications = (context.current_publications || []).filter((id: string) => id !== docId);
        } else if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        } else if (docType === 'foreign_patent') {
          context.current_foreign_patents = (context.current_foreign_patents || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    // Listen for storage updates to refresh active document status
    const handleStorageUpdate = () => {
      setStorageUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('storage-update', handleStorageUpdate);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
      window.removeEventListener('storage-update', handleStorageUpdate);
    };
  }, [addNotification]);

  // Load collections on mount and listen for cross-component updates
  useEffect(() => {
    collectionsApi.list().then(setCollections).catch(() => {});
    const handleCollectionsUpdate = () => {
      collectionsApi.list().then(setCollections).catch(() => {});
    };
    window.addEventListener('collections-updated', handleCollectionsUpdate);
    return () => {
      window.removeEventListener('collections-updated', handleCollectionsUpdate);
    };
  }, []);

  // When a collection is selected, create placeholder entries for documents not already loaded
  useEffect(() => {
    if (selectedCollectionId === 'all') {
      setCollectionPlaceholders([]);
      return;
    }

    const selectedCol = collections.find(c => c.collection_id === selectedCollectionId);
    if (!selectedCol || selectedCol.resource_ids.length === 0) {
      setCollectionPlaceholders([]);
      return;
    }

    const loadedResourceIds = new Set(sessionDocuments.map(d => d.resource_id));
    const missingIds = selectedCol.resource_ids.filter(rid => !loadedResourceIds.has(rid));

    if (missingIds.length === 0) {
      setCollectionPlaceholders([]);
      return;
    }

    // Build placeholder SessionDocument entries for missing docs
    const placeholders: SessionDocument[] = missingIds.map(rid => {
      let fileType = 'upload';
      if (rid.startsWith('patent-')) fileType = 'patent';
      else if (rid.startsWith('publication-')) fileType = 'publication';
      else if (rid.startsWith('application-')) fileType = 'application';
      else if (rid.startsWith('foreign_patent-')) fileType = 'foreign_patent';

      return {
        resource_id: rid,
        file_name: formatResourceId(rid),
        file_type: fileType,
        title: formatResourceId(rid),
      };
    });

    setCollectionPlaceholders(placeholders);
  }, [selectedCollectionId, collections, sessionDocuments]);

  const loadSessionDocuments = async (override_cache = false) => {
    setLoading(true);
    try {
      const documents = await documentApi.fetchSessionDocuments(documentLimit);
      setSessionDocuments(documents);
      // Check if there might be more documents (if we got exactly the limit, there might be more)
      setHasMoreDocuments(documents.length === documentLimit);
    } catch (error) {
      console.error('Failed to load session documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreDocuments = async () => {
    setLoadingMore(true);
    try {
      const newLimit = documentLimit + 50;
      const documents = await documentApi.fetchSessionDocuments(newLimit);
      setSessionDocuments(documents);
      setDocumentLimit(newLimit);
      // Check if there might be more documents
      setHasMoreDocuments(documents.length === newLimit);
    } catch (error) {
      console.error('Failed to load more documents:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Perform semantic (full text) search
  const performSemanticSearch = async (query: string) => {
    const requestId = ++semanticRequestRef.current;
    const searchIdentity = searchIdentityRef.current;
    if (!query.trim() || query.length < 2) {
      setSemanticResults([]);
      setSemanticSearchError('');
      return;
    }

    setHasSearchedOnce(true);
    setIsSemanticSearching(true);
    setSemanticSearchError('');

    try {
      // When a collection is selected, scope the search to only those documents
      // and request enough results to rank all of them
      const selectedCol = selectedCollectionId !== 'all'
        ? collections.find(c => c.collection_id === selectedCollectionId)
        : null;
      const collectionResourceIds = selectedCol?.resource_ids;
      const topK = collectionResourceIds ? Math.max(collectionResourceIds.length, 20) : 20;

      const response = await documentApi.semanticSearchDocuments(query.trim(), topK, collectionResourceIds);
      if (requestId !== semanticRequestRef.current || searchIdentity !== searchIdentityRef.current) return;
      if (response.error) {
        setSemanticSearchError(response.error);
        setSemanticResults([]);
      } else {
        const results = response.results || [];
        setSemanticResults(results);
        if (response.message && results.length === 0) {
          setSemanticSearchError(response.message);
        } else {
          setSemanticSearchError('');
        }
      }
    } catch (error) {
      if (requestId !== semanticRequestRef.current || searchIdentity !== searchIdentityRef.current) return;
      console.error('Semantic search failed:', error);
      setSemanticSearchError('Search failed. Please try again.');
      setSemanticResults([]);
    } finally {
      if (requestId === semanticRequestRef.current) setIsSemanticSearching(false);
    }
  };

  // Handle search mode toggle
  const handleSearchModeChange = (newIsFullTextSearch: boolean) => {
    setIsFullTextSearch(newIsFullTextSearch);
    setSemanticResults([]);
    setSemanticSearchError('');
    setHasSearchedOnce(false);
  };

  const handleDelete = (doc: SessionDocument) => {
    setDocumentToDelete(doc);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    console.log('[DocumentsPage] Attempting to delete document:', {
      file_type: documentToDelete.file_type,
      resource_id: documentToDelete.resource_id,
      file_name: documentToDelete.file_name
    });

    setIsDeleting(true);
    try {
      await documentApi.deleteDocument(documentToDelete.file_type, documentToDelete.resource_id);
      console.log('[DocumentsPage] Delete API call successful');
      setSessionDocuments(prev => prev.filter(d => d.resource_id !== documentToDelete.resource_id));

      // Update session_context to remove the deleted document
      try {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        // Extract just the ID without type prefix
        const docId = documentToDelete.resource_id.replace(/^(patent-|publication-|application-|upload-)/, '');
        const docType = documentToDelete.file_type.toLowerCase();

        // Remove from resource_ids and resource_names
        if (context.resource_ids) {
          context.resource_ids = context.resource_ids.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
        }
        if (context.resource_names) {
          context.resource_names = context.resource_names.filter((name: string) => name !== documentToDelete.file_name);
        }
        if (context.new_resource_ids) {
          context.new_resource_ids = context.new_resource_ids.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
        }
        if (context.new_resource_names) {
          context.new_resource_names = context.new_resource_names.filter((name: string) => name !== documentToDelete.file_name);
        }

        // Remove from type-specific arrays
        if (docType === 'patent' && context.current_patents) {
          context.current_patents = context.current_patents.filter((id: string) => id !== docId);
        }
        if (docType === 'publication' && context.current_publications) {
          context.current_publications = context.current_publications.filter((id: string) => id !== docId);
        }
        if (docType === 'application' && context.current_applications) {
          context.current_applications = context.current_applications.filter((id: string) => id !== docId);
        }
        if (docType === 'foreign_patent' && context.current_foreign_patents) {
          context.current_foreign_patents = context.current_foreign_patents.filter((id: string) => id !== docId);
        }

        // Remove from extracted_resources if present
        if (context.extracted_resources) {
          context.extracted_resources = context.extracted_resources.filter((resource: any) => {
            const resourceId = resource.resource_id || resource.id || resource;
            return resourceId !== docId && resourceId !== documentToDelete.resource_id;
          });
        }

        // Clear singular references if they match the deleted document
        if (context.current_patent === docId) context.current_patent = null;
        if (context.current_publication === docId) context.current_publication = null;
        if (context.current_application === docId) context.current_application = null;

        localStorage.setItem('session_context', JSON.stringify(context));

        // Also update separate localStorage keys for new_resource_ids and new_resource_names
        try {
          const storedNewIds = localStorage.getItem('new_resource_ids');
          if (storedNewIds) {
            const newIds = JSON.parse(storedNewIds);
            if (Array.isArray(newIds)) {
              const filteredIds = newIds.filter((id: string) => id !== docId && id !== documentToDelete.resource_id);
              localStorage.setItem('new_resource_ids', JSON.stringify(filteredIds));
            }
          }

          const storedNewNames = localStorage.getItem('new_resource_names');
          if (storedNewNames) {
            const newNames = JSON.parse(storedNewNames);
            if (Array.isArray(newNames)) {
              const filteredNames = newNames.filter((name: string) => name !== documentToDelete.file_name);
              localStorage.setItem('new_resource_names', JSON.stringify(filteredNames));
            }
          }
        } catch (storageError) {
          console.error('Failed to update new_resource localStorage keys:', storageError);
        }

        window.dispatchEvent(new Event('storage-update'));
        console.log('[DocumentsPage] Updated session_context after deleting document:', documentToDelete.resource_id);
      } catch (contextError) {
        console.error('Failed to update session_context after delete:', contextError);
      }

      addNotification({
        type: 'success',
        title: 'Document Deleted',
        message: `${documentToDelete.file_name} has been removed from your session.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('[DocumentsPage] Failed to delete document:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete document. Please try again.';
      addNotification({
        type: 'error',
        title: 'Delete Failed',
        message: errorMessage,
        duration: 5000,
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDocumentToDelete(null);
    }
  };

  const handleDownloadUpload = (doc: SessionDocument) => {
    try {
      const resourceId = doc.resource_id || '';
      const fileName = doc.file_name || 'download';
      const idToken = localStorage.getItem('id_token');

      if (!resourceId || !idToken) {
        addNotification({
          type: 'error',
          title: 'Download Failed',
          message: 'Unable to download file. Missing required information.',
        });
        return;
      }

      // Open download URL in new tab - the backend will handle the file download
      const downloadUrl = `${API_CONFIG.baseUrl}/doc/download_upload?resource_id=${encodeURIComponent(resourceId)}&id_token=${encodeURIComponent(idToken)}`;
      window.open(downloadUrl, '_blank');

      addNotification({
        type: 'success',
        title: 'Download Started',
        message: `Downloading ${fileName}...`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to download upload:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Unable to download the file. Please try again.',
        duration: 5000,
      });
    }
  };

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    console.log('[DocumentsPage] Starting poll via global store:', { jobId, docType, docId });
    // Use global polling store that persists across navigation
    startPolling(jobId, docType, docId);
  };

  const getDocumentUrl = (doc: SessionDocument) => {
    // Get clean document ID
    let docId = doc.resource_id.replace(/^(patent-|publication-|application-)/, '');

    // For patents/publications, ensure US prefix
    if (['patent', 'publication', 'application'].includes(doc.file_type)) {
      const number = docId.replace(/,/g, '').replace(/\//g, '');
      // If it's a publication (starts with 20), use as-is with US prefix
      if (number.startsWith('20') && number.length >= 11) {
        return `/US${number}`;
      }
      // For patents (7-8 digits), add US prefix
      return `/US${number}`;
    }

    return null;
  };

  const handleOpenInNewTab = (doc: SessionDocument) => {
    const url = getDocumentUrl(doc);
    if (url) {
      window.open(url, '_blank');
    }
  };

  const addToActiveDocuments = async (doc: SessionDocument) => {
    try {
      // Handle uploads differently - add directly to session context
      if (doc.file_type === 'upload') {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        const resourceNames = context.resource_names || [];
        const resourceIds = context.resource_ids || [];

        // Check if already added
        if (resourceNames.includes(doc.file_name)) {
          addNotification({
            type: 'info',
            title: 'Already Added',
            message: `${doc.file_name} is already in your session documents.`,
            duration: 3000,
          });
          return;
        }

        // Add to context
        resourceNames.push(doc.file_name);
        resourceIds.push(doc.resource_id);

        context.resource_names = resourceNames;
        context.resource_ids = resourceIds;

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addNotification({
          type: 'success',
          title: 'Added to session',
          message: `${doc.file_name} has been added to your session.`,
          duration: 3000,
        });

        return;
      }

      // Only handle patent, publication, application, and foreign_patent types through the API
      if (!['patent', 'publication', 'application', 'foreign_patent'].includes(doc.file_type)) {
        addNotification({
          type: 'info',
          title: 'Not Supported',
          message: 'This document type cannot be added to session documents.',
          duration: 5000,
        });
        return;
      }

      // Clean the document ID - remove prefixes
      let cleanedId = doc.resource_id
        .replace(/^(patent-|publication-|application-|foreign_patent-)/, '');

      // Additional cleaning for US patent types only
      if (['patent', 'publication', 'application'].includes(doc.file_type)) {
        cleanedId = cleanedId
          .replace(/,/g, '')
          .replace(/\//g, '')
          .replace(/^US/i, '')
          .replace(/[A-Z]\d*$/i, '');
      }

      // Check if already in context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

      let contextKey: string;
      if (doc.file_type === 'patent') {
        contextKey = 'current_patents';
      } else if (doc.file_type === 'publication') {
        contextKey = 'current_publications';
      } else if (doc.file_type === 'application') {
        contextKey = 'current_applications';
      } else if (doc.file_type === 'foreign_patent') {
        contextKey = 'current_foreign_patents';
      } else {
        return;
      }

      const currentDocs = Array.isArray(context[contextKey]) ? context[contextKey] : [];
      if (currentDocs.includes(cleanedId)) {
        addNotification({
          type: 'info',
          title: 'Already Added',
          message: `${cleanedId} is already in your session documents.`,
          duration: 3000,
        });
        return;
      }

      // Call the API to fetch and load the document
      const response = await documentApi.fetchAndLoadDocument(doc.file_type, cleanedId);

      if (response?.job_id) {
        // Add to session context immediately
        context[contextKey] = [...currentDocs, cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        // Show processing notification (will auto-dismiss for non-success)
        const resourceTypeName = doc.file_type === 'foreign_patent'
          ? 'Foreign Patent'
          : doc.file_type.charAt(0).toUpperCase() + doc.file_type.slice(1);
        addNotification({
          type: 'info',
          title: `Loading ${resourceTypeName}`,
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading state
        addLoadingDocument(cleanedId, resourceTypeName, cleanedId);

        // Start polling for job status
        pollJobStatus(response.job_id, doc.file_type, cleanedId);
      }
    } catch (error) {
      console.error('Failed to add to session documents:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add document to session documents. Please try again.',
        duration: 5000,
      });
    }
  };

  // Check if a document is already in active documents
  const isDocumentInActive = (doc: SessionDocument): boolean => {
    try {
      // Handle uploads
      if (doc.file_type === 'upload') {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
        const resourceNames = context.resource_names || [];
        return resourceNames.includes(doc.file_name);
      }

      // Handle patents, publications, applications, and foreign patents
      if (['patent', 'publication', 'application', 'foreign_patent'].includes(doc.file_type)) {
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        // Clean the document ID - same logic as in addToActiveDocuments
        let cleanedId = doc.resource_id
          .replace(/^(patent-|publication-|application-|foreign_patent-)/, '');

        // Additional cleaning for US patent types only
        if (['patent', 'publication', 'application'].includes(doc.file_type)) {
          cleanedId = cleanedId
            .replace(/,/g, '')
            .replace(/\//g, '')
            .replace(/^US/i, '')
            .replace(/[A-Z]\d*$/i, '');
        }

        let contextKey: string;
        if (doc.file_type === 'patent') {
          contextKey = 'current_patents';
        } else if (doc.file_type === 'publication') {
          contextKey = 'current_publications';
        } else if (doc.file_type === 'application') {
          contextKey = 'current_applications';
        } else if (doc.file_type === 'foreign_patent') {
          contextKey = 'current_foreign_patents';
        } else {
          return false;
        }

        const currentDocs = Array.isArray(context[contextKey]) ? context[contextKey] : [];
        return currentDocs.includes(cleanedId);
      }

      return false;
    } catch (error) {
      console.error('Error checking if document is in active:', error);
      return false;
    }
  };

  // Convert session documents to display format
  // Merge placeholder collection docs with sessionDocuments for collection filtering
  const mergedSessionDocuments = (() => {
    if (collectionPlaceholders.length === 0) return sessionDocuments;
    return [...sessionDocuments, ...collectionPlaceholders];
  })();

  const allDocuments = mergedSessionDocuments
    .filter(doc => {
      // Filter out invalid documents (must have file_name and resource_id)
      // Also filter out test/corrupted entries
      return doc.file_name &&
             doc.resource_id &&
             doc.file_type &&
             doc.file_name !== 'Add to Active' && // Filter out corrupted entries
             doc.resource_id !== '101'; // Filter out test entries
    })
    .map(doc => {
      // Use title if available (for patents/publications), otherwise use file_name (for uploads)
      let displayTitle = doc.title || doc.file_name;
      // Remove type prefixes from title for cleaner display
      if (displayTitle) {
        displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
      }
      return {
        ...doc,
        id: doc.resource_id,
        title: displayTitle,
        type: doc.file_type,
        status: 'Loaded',
        lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
        onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
        lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
      };
    });

  // Determine documents to display based on search mode
  // Note: Backend already returns results sorted by relevance (distance field, lower = more relevant)
  const documentsToDisplay = isFullTextSearch && semanticResults.length > 0
    ? semanticResults.map(doc => {
        // Use title if available (for patents/publications), otherwise use file_name (for uploads)
        let displayTitle = doc.title || doc.file_name;
        // Remove type prefixes from title for cleaner display
        if (displayTitle) {
          displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
        }
        return {
          ...doc,
          id: doc.resource_id,
          title: displayTitle,
          type: doc.file_type,
          status: 'Loaded',
          lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
          onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
          lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
          _sourceDoc: doc, // Keep reference to original document
        };
      })
    : allDocuments;

  // Helper function to safely get source document
  const getSourceDoc = (doc: any): SessionDocument | undefined => {
    // If doc has _sourceDoc (from semantic search), use it
    if (doc._sourceDoc) {
      return doc._sourceDoc;
    }
    // Otherwise try to find in sessionDocuments
    return sessionDocuments.find(d => d.resource_id === doc.id);
  };

  // ─── Collection helpers ──────────────────────────────────────────────────────

  const createCollection = async (name: string) => {
    const collectionId = crypto.randomUUID();
    await collectionsApi.create(collectionId, name);
    setCollections(prev => [...prev, { collection_id: collectionId, name, created_date: new Date().toISOString(), resource_ids: [] }]);
    window.dispatchEvent(new Event('collections-updated'));
    return collectionId;
  };

  const toggleDocInCollection = async (collectionId: string, resourceId: string) => {
    const col = collections.find(c => c.collection_id === collectionId);
    if (!col) return;
    const isIn = col.resource_ids.includes(resourceId);
    if (isIn) {
      await collectionsApi.removeDoc(collectionId, resourceId);
      setCollections(prev => prev.map(c =>
        c.collection_id === collectionId
          ? { ...c, resource_ids: c.resource_ids.filter(id => id !== resourceId) }
          : c
      ));
    } else {
      await collectionsApi.addDoc(collectionId, resourceId);
      setCollections(prev => prev.map(c =>
        c.collection_id === collectionId
          ? { ...c, resource_ids: [...c.resource_ids, resourceId] }
          : c
      ));
    }
    window.dispatchEvent(new Event('collections-updated'));
  };

  const isInAnyCollection = (resourceId: string) => {
    return collections.some(c => c.resource_ids.includes(resourceId));
  };

  const openCollectionDialog = (doc: SessionDocument) => {
    setCollectionDialogDoc(doc);
    setShowCollectionDialog(true);
    setNewCollectionName('');
  };

  const getDocIcon = (type: string, className: string) => {
    const t = (type || '').toLowerCase();
    if (t === 'upload') return <Upload className={className} />;
    if (t === 'foreign patent' || t === 'foreign_patent') return <Globe className={className} />;
    if (t === 'publication') return <BookOpen className={className} />;
    if (t === 'patent') return <ScrollText className={className} />;
    if (t === 'mpep') return <Scale className={className} />;
    if (t === 'cfr' || t === 'usc') return <BookMarked className={className} />;
    return <FileText className={className} />;
  };

  const getDocIconBg = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t === 'upload') return 'bg-amber-50';
    if (t === 'foreign patent' || t === 'foreign_patent') return 'bg-green-50';
    if (t === 'publication') return 'bg-purple-50';
    if (t === 'patent') return 'bg-blue-50';
    if (t === 'mpep') return 'bg-orange-50';
    if (t === 'cfr' || t === 'usc') return 'bg-cyan-50';
    return 'bg-gray-50';
  };

  const getDocIconColor = (type: string) => {
    const t = (type || '').toLowerCase();
    if (t === 'upload') return 'text-amber-600';
    if (t === 'foreign patent' || t === 'foreign_patent') return 'text-green-600';
    if (t === 'publication') return 'text-purple-600';
    if (t === 'patent') return 'text-blue-600';
    if (t === 'mpep') return 'text-orange-600';
    if (t === 'cfr' || t === 'usc') return 'text-cyan-600';
    return 'text-gray-500';
  };

  // Filter documents based on search and filter criteria
  const filteredDocuments = (() => {
    // If metadata search is active and has results, use those
    if (!isFullTextSearch && searchQuery.trim().length >= 2 && metadataSearchResults.length > 0) {
      // Transform metadata search results same as semantic search results
      return metadataSearchResults
        .map(doc => {
          // Use title if available (for patents/publications), otherwise use file_name (for uploads)
          let displayTitle = doc.title || doc.file_name;
          // Remove type prefixes from title for cleaner display
          if (displayTitle) {
            displayTitle = displayTitle.replace(/^(patent-|publication-|application-|foreign_patent[\s-])/gi, '');
          }
          return {
            ...doc,
            id: doc.resource_id,
            title: displayTitle,
            type: doc.file_type,
            status: 'Loaded',
            lastAccessed: doc.last_accessed_date ? formatDateTime(doc.last_accessed_date) : 'Never',
            onboardedDate: doc.create_date ? formatDate(doc.create_date) : 'Unknown',
            lastAccessedTimestamp: doc.last_accessed_date ? new Date(doc.last_accessed_date + (doc.last_accessed_date.endsWith('Z') || doc.last_accessed_date.match(/[+-]\d{2}:\d{2}$/) ? '' : 'Z')).getTime() : 0,
            _sourceDoc: doc, // Keep reference to original document
          };
        })
        .filter(doc => {
          const matchesFilter = filterType === 'all' || (doc.file_type || '').toLowerCase() === filterType.toLowerCase();
          const matchesCollection = selectedCollectionId === 'all' || collections.find(c => c.collection_id === selectedCollectionId)?.resource_ids.includes(doc.resource_id);
          return matchesFilter && matchesCollection;
        });
    }

    // Otherwise use regular filtering
    return documentsToDisplay.filter(doc => {
      // For semantic search, don't filter by text search since results are already filtered
      const matchesSearch = isFullTextSearch ? true :
        !searchQuery.trim() ||
        (doc.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.resource_id || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === 'all' || (doc.type || '').toLowerCase() === filterType.toLowerCase();
      const matchesCollection = selectedCollectionId === 'all' || collections.find(c => c.collection_id === selectedCollectionId)?.resource_ids.includes(doc.resource_id || doc.id);
      return matchesSearch && matchesFilter && matchesCollection;
    });
  })()
    // Sort by most recently accessed (unless in semantic/metadata search mode where relevance matters)
    .sort((a, b) => {
      if (isFullTextSearch && semanticResults.length > 0) {
        // Keep semantic search results in relevance order
        return 0;
      }
      if (!isFullTextSearch && metadataSearchResults.length > 0) {
        // Keep metadata search results in relevance order (backend sorted)
        return 0;
      }
      // Sort by last accessed timestamp, most recent first
      return (b.lastAccessedTimestamp || 0) - (a.lastAccessedTimestamp || 0);
    });

  // Show minimized view when document viewer is open
  if (isDocumentViewerOpen) {
    return (
      <div className={`flex flex-col h-full bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden ${isCollapsed ? 'w-12' : 'w-[320px]'}`}>
        {/* Collapse Toggle */}
        <div className="border-b border-gray-200 px-3 py-4 flex items-center justify-between">
          {!isCollapsed && <span className="text-sm text-gray-600">Documents</span>}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {!isCollapsed && (
          <>
            {/* Compact Search */}
            <div className="border-b border-gray-200 px-3 py-3">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 h-9 text-sm"
                />
                {(isSemanticSearching || isMetadataSearching) && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                  </div>
                )}
              </div>
              {/* Results count for compact mode */}
              {!isFullTextSearch && searchQuery.trim().length >= 2 && !isMetadataSearching && (
                <div className="text-xs text-gray-600 mt-1">
                  {metadataSearchError ? <span role="alert" className="text-red-700">{metadataSearchError}</span> : metadataSearchResults.length > 0 ? (
                    <span className="text-green-700">
                      ✓ {metadataSearchResults.length} match{metadataSearchResults.length !== 1 ? 'es' : ''}
                    </span>
                  ) : (
                    <span className="text-amber-700">No matches</span>
                  )}
                </div>
              )}
            </div>

            {/* Compact Document List */}
            <div className="flex-1 overflow-hidden w-full max-w-[320px]">
              <ScrollArea className="h-full" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="p-2 space-y-1" style={{ width: '100%', maxWidth: '330px', minWidth: '0' }}>
                {loading ? (
                  <div className="flex flex-col items-center justify-center text-sm text-gray-500 py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mb-2" />
                    <span>Loading documents...</span>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-4">No documents found</div>
                ) : (
                  filteredDocuments.map((doc) => {
                    const isPlaceholder = !doc.create_date && !doc.last_accessed_date && collectionPlaceholders.some(p => p.resource_id === doc.resource_id);

                    return isPlaceholder ? (
                      <div
                        key={doc.id}
                        className="w-full text-left p-3 rounded-md border border-dashed border-amber-300 bg-amber-50/50 block"
                      >
                        <div className="flex items-start gap-2 w-full">
                          {getDocIcon(doc.type, `w-4 h-4 ${getDocIconColor(doc.type)} mt-0.5 flex-shrink-0`)}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 font-medium mb-1 truncate" title={doc.title}>
                              {doc.title}
                            </div>
                            <div className="text-xs text-amber-600 mb-1">Not yet in your library</div>
                            <Button
                              size="sm"
                              onClick={() => {
                                const placeholderDoc: SessionDocument = {
                                  resource_id: doc.resource_id,
                                  file_name: doc.title,
                                  file_type: doc.type,
                                };
                                addToActiveDocuments(placeholderDoc);
                              }}
                              className="h-6 text-xs gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Add to library
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : (
                    <button
                      key={doc.id}
                      className="w-full text-left p-3 rounded-md hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200 block"
                      onClick={() => onDocumentSelect(doc)}
                    >
                      <div className="flex items-start gap-2 mb-2 w-full">
                        {getDocIcon(doc.type, `w-4 h-4 ${getDocIconColor(doc.type)} mt-0.5 flex-shrink-0`)}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 font-medium mb-1 truncate" title={doc.title}>
                            {doc.title}
                          </div>
                          <div className="text-xs text-gray-500 truncate" title={formatResourceId(doc.resource_id)}>
                            {formatResourceId(doc.resource_id)}
                          </div>
                        </div>
                      </div>
                    </button>
                    );
                  })
                )}
              </div>
              </ScrollArea>
            </div>
          </>
        )}
      </div>
    );
  }

  // Show full view when document viewer is not open
  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Toolbar */}
      <div className={`flex-shrink-0 border-b border-gray-200 bg-white ${compactMode ? 'px-4 py-2' : 'px-6 py-4'}`}>
        <div className={`flex items-center justify-between gap-4 ${compactMode ? 'mb-2' : 'mb-4'}`}>
          <div className="flex-1 flex gap-2 min-w-0">
            <div className="relative flex-1 min-w-[160px] max-w-2xl">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                placeholder={isFullTextSearch ?
                  "Find relevant documents by searching their full text content..." :
                  "Search documents by filename, type, ID..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (isFullTextSearch) {
                      performSemanticSearch(searchQuery);
                    }
                    // Metadata search is already triggered by debounce
                  }
                }}
                className={`pl-9 ${isFullTextSearch ? 'border-blue-400 bg-blue-50' : ''}`}
                aria-label="Search documents"
                aria-busy={isSemanticSearching || isMetadataSearching}
              />
              {searchQuery && !isSemanticSearching && !isMetadataSearching && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSemanticResults([]);
                    setSemanticSearchError('');
                    setMetadataSearchResults([]);
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 pointer-events-auto"
                  type="button"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Search Button (for both metadata and full-text search) */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                if (isFullTextSearch) {
                  performSemanticSearch(searchQuery);
                } else {
                  // Force metadata search by adding/removing space to trigger debounce
                  if (searchQuery.trim().length >= 2) setMetadataSearchVersion(previous => previous + 1);
                }
              }}
              disabled={searchQuery.trim().length < 2}
              className="gap-2"
            >
              {(isSemanticSearching || isMetadataSearching) ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              Search
            </Button>

            {/* Full Text Search Toggle */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Full Text Search
              </label>
              <button
                onClick={() => handleSearchModeChange(!isFullTextSearch)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all duration-200 ${
                  isFullTextSearch ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-all duration-200 ${
                    isFullTextSearch ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Metadata Search Results Info */}
          {!isFullTextSearch && searchQuery.trim().length >= 2 && !isMetadataSearching && (
            <div className="text-xs text-gray-600">
              {metadataSearchError ? <span role="alert" className="text-red-700">{metadataSearchError}</span> : metadataSearchResults.length > 0 ? (
                <span className="text-green-700">
                  ✓ Found {metadataSearchResults.length} match{metadataSearchResults.length !== 1 ? 'es' : ''}
                </span>
              ) : (
                <span className="text-amber-700">No documents found matching "{searchQuery}"</span>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            {onAddDocuments && (
              <Button
                onClick={onAddDocuments}
                className="gap-2"
                title="Add Document"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden 2xl:inline">Add Document</span>
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowMultiDocQA(true)}
              className="flex items-center gap-1.5"
              title="Multi-Doc Q&A"
            >
              <MessageSquarePlus className="w-4 h-4" />
              <span className="hidden 2xl:inline">Multi-Doc Q&A</span>
            </Button>

            <Select value={selectedCollectionId} onValueChange={setSelectedCollectionId}>
              <SelectTrigger className="w-[200px]">
                <Bookmark className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Collections" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Collections</SelectItem>
                {collections.map(col => (
                  <SelectItem key={col.collection_id} value={col.collection_id}>
                    {col.name} ({col.resource_ids.length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              onClick={() => setShowCollectionsManager(true)}
              variant="outline"
              size="icon"
              className="h-9 w-9"
              title="Manage collections"
            >
              <Bookmark className="w-4 h-4" />
            </Button>

            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Documents</SelectItem>
                <SelectItem value="patent">Patents</SelectItem>
                <SelectItem value="publication">Publications</SelectItem>
                <SelectItem value="foreign_patent">Foreign Patents</SelectItem>
                <SelectItem value="upload">Uploads</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center gap-1 border border-gray-200 rounded-lg p-1">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('grid')}
              >
                <Grid3x3 className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 text-sm">
          {searchQuery && isFullTextSearch ? (
            <span className={`${semanticResults.length > 0 ? 'text-blue-600' : 'text-gray-600'}`}>
              {isSemanticSearching ? (
                "Searching..."
              ) : semanticSearchError ? (
                <span className="text-red-600">{semanticSearchError}</span>
              ) : semanticResults.length > 0 ? (
                <>
                  Found {semanticResults.length} document{semanticResults.length !== 1 ? 's' : ''} matching "{searchQuery}"
                  <span className="ml-2 text-xs text-gray-500">(sorted by relevance)</span>
                </>
              ) : hasSearchedOnce ? (
                `No documents found matching "${searchQuery}"`
              ) : (
                `Press Enter or click Search to find documents matching "${searchQuery}"`
              )}
            </span>
          ) : searchQuery ? (
            <span className="text-gray-600">
              Found {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </span>
          ) : (
            <>
              <span className="text-gray-600">{filteredDocuments.length} documents</span>
              <span className="text-gray-300">•</span>
              <span className="text-gray-600">Last updated: Today</span>
            </>
          )}
        </div>
      </div>

      {/* Documents Grid/List */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto overflow-x-hidden">
          <div className={`w-full ${compactMode ? 'p-3' : 'p-6'}`}>
          {loading ? (
            <div className={`flex flex-col items-center justify-center ${compactMode ? 'py-8' : 'py-12'}`}>
              <Loader2 className={`${compactMode ? 'w-8 h-8 mb-2' : 'w-10 h-10 mb-3'} text-blue-600 animate-spin`} />
              <p className={`text-gray-600 ${compactMode ? 'text-sm' : ''}`}>Loading documents...</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className={`grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 ${compactMode ? 'gap-2' : 'gap-4'}`}>
              {filteredDocuments.map((doc) => {
                const isPlaceholder = !doc.create_date && !doc.last_accessed_date && collectionPlaceholders.some(p => p.resource_id === doc.resource_id);

                return isPlaceholder ? (
                  <Card
                    key={doc.id}
                    className="border-dashed border-amber-300 bg-amber-50/50"
                  >
                    <CardHeader className="pb-1">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200">
                          {doc.type}
                        </Badge>
                        <Button
                          size="sm"
                          onClick={() => {
                            const placeholderDoc: SessionDocument = {
                              resource_id: doc.resource_id,
                              file_name: doc.title,
                              file_type: doc.type,
                            };
                            addToActiveDocuments(placeholderDoc);
                          }}
                          className="h-7 text-xs gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add to library
                        </Button>
                      </div>
                      <CardTitle className="text-base leading-tight line-clamp-2 break-words" title={doc.title}>
                        {doc.title}
                      </CardTitle>
                      <CardDescription className="space-y-1 mt-2">
                        <div className="text-xs">{formatResourceId(doc.resource_id)}</div>
                        <div className="text-xs text-amber-600">Not yet in your library</div>
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ) : (
                <Card
                  key={doc.id}
                  className="cursor-pointer hover:shadow-md transition-shadow group"
                  onClick={() => onDocumentSelect(doc)}
                >
                  <CardHeader className="pb-1">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200">
                        {doc.type}
                      </Badge>
                      <div className="flex items-start justify-between gap-2">
                        {(() => {
                          const sourceDoc = getSourceDoc(doc);
                          const isInActive = sourceDoc ? isDocumentInActive(sourceDoc) : false;

                          return isInActive ? (
                            <div className="flex items-center gap-1 text-xs text-green-600 h-7 px-2">
                              <CheckCircle2 className="w-3 h-3" />
                              <span>In this session</span>
                            </div>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="h-7 text-xs gap-1"
                            >
                              <Plus className="w-3 h-3" />
                              Add to session
                            </Button>
                          );
                        })()}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); const sourceDoc = getSourceDoc(doc); if (sourceDoc) openCollectionDialog(sourceDoc); }}
                        >
                          {isInAnyCollection(doc.resource_id || doc.id)
                            ? <BookmarkCheck className="w-4 h-4 text-blue-600" />
                            : <Bookmark className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add to session
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) openCollectionDialog(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              {isInAnyCollection(doc.resource_id || doc.id)
                                ? <><BookmarkCheck className="w-4 h-4 mr-2" /> Manage Collections</>
                                : <><FolderPlus className="w-4 h-4 mr-2" /> Add to Collection</>}
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && getDocumentUrl(sourceDoc) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (sourceDoc) handleOpenInNewTab(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Open in New Tab
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentSelect(doc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Document
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && sourceDoc.file_type?.toLowerCase() === 'upload' && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadUpload(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download File
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) handleDelete(sourceDoc);
                              }}
                              className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    <CardTitle className="text-base leading-tight line-clamp-2 break-words" title={doc.title}>
                      {doc.title}
                    </CardTitle>
                    <CardDescription className="space-y-1 mt-2">
                      <div className="text-xs">{formatResourceId(doc.resource_id)}</div>
                      <div className="text-xs">Type: {doc.type}</div>
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {doc.summary && (
                      <p className="text-sm text-gray-600 line-clamp-3">{doc.summary}</p>
                    )}

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      {doc.onboardedDate && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          Onboarded: {doc.onboardedDate}
                        </div>
                      )}
                      {doc.file_size && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5" />
                          {(doc.file_size / 1024).toFixed(1)} KB
                        </div>
                      )}
                    </div>

                    {doc.lastAccessed && doc.lastAccessed !== 'Never' && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="text-xs text-gray-500">
                          Last accessed: {doc.lastAccessed}
                        </div>
                      </div>
                    )}

                    {/* Semantic Search Preview */}
                    {isFullTextSearch && doc.chunk_preview && (
                      <div className="pt-2 border-t border-blue-200 bg-blue-50 rounded-lg p-2">
                        <div className="flex items-start gap-2">
                          <div className="flex flex-col gap-1">
                            <Badge className="bg-blue-600 text-white text-xs">MATCH</Badge>
                            {doc._sourceDoc?.num_chunks !== undefined && doc._sourceDoc?.total_chunks !== undefined && (
                              <Badge className="bg-green-600 text-white text-xs" title="Relevant sections / Total sections">
                                {doc._sourceDoc.num_chunks}/{doc._sourceDoc.total_chunks}
                              </Badge>
                            )}
                            {doc._sourceDoc?.tf_score !== undefined && (
                              <Badge className="bg-purple-600 text-white text-xs" title="Relevance density (TF score)">
                                {(doc._sourceDoc.tf_score * 100).toFixed(0)}%
                              </Badge>
                            )}
                            {doc._sourceDoc?.distance !== undefined && (
                              <Badge className="bg-gray-600 text-white text-xs" title="Best match quality">
                                {(1 - doc._sourceDoc.distance).toFixed(3)}
                              </Badge>
                            )}
                          </div>
                          <div className="flex-1">
                            {doc.section_title && (
                              <div className="text-xs font-medium text-blue-900 mb-1 prose prose-sm max-w-none [&>p]:inline">
                                <Markdown>{fixMarkdownFormatting(doc.section_title)}</Markdown>
                              </div>
                            )}
                            <div className="text-xs text-blue-900 leading-relaxed line-clamp-3 prose prose-sm max-w-none [&>p]:inline">
                              <Markdown>{fixMarkdownFormatting(doc.chunk_preview)}</Markdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredDocuments.map((doc) => {
                const isPlaceholder = !doc.create_date && !doc.last_accessed_date && collectionPlaceholders.some(p => p.resource_id === doc.resource_id);

                return isPlaceholder ? (
                  <div
                    key={doc.id}
                    className="w-full border border-dashed border-amber-300 bg-amber-50/50 rounded-lg p-4 overflow-hidden"
                  >
                    <div className="flex items-center gap-4 min-w-0 w-full">
                      <div className={`w-12 h-12 ${getDocIconBg(doc.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                        {getDocIcon(doc.type, `w-6 h-6 ${getDocIconColor(doc.type)}`)}
                      </div>
                      <div className="flex-1 min-w-0 overflow-hidden">
                        <h3 className="text-gray-900 font-semibold truncate">{doc.title}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                            {doc.type}
                          </Badge>
                          <span className="text-xs text-gray-400">Not yet in your library</span>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          const placeholderDoc: SessionDocument = {
                            resource_id: doc.resource_id,
                            file_name: doc.title,
                            file_type: doc.type,
                          };
                          addToActiveDocuments(placeholderDoc);
                        }}
                        className="gap-1 flex-shrink-0"
                      >
                        <Plus className="w-3 h-3" />
                        Add to library
                      </Button>
                    </div>
                  </div>
                ) : (
                <div
                  key={doc.id}
                  className="w-full border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer group overflow-hidden"
                  onClick={() => onDocumentSelect(doc)}
                >
                  <div className="flex items-start gap-4 min-w-0 w-full">
                    <div className={`w-12 h-12 ${getDocIconBg(doc.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                      {getDocIcon(doc.type, `w-6 h-6 ${getDocIconColor(doc.type)}`)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 min-w-0">
                            <h3 className="text-gray-900 font-semibold truncate min-w-0" title={doc.title}>{doc.title}</h3>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              const isInActive = sourceDoc ? isDocumentInActive(sourceDoc) : false;

                              return isInActive ? (
                                <div className="flex items-center gap-1 text-xs text-green-600 h-7 px-2 flex-shrink-0">
                                  <CheckCircle2 className="w-3 h-3" />
                                  <span>In this session</span>
                                </div>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (sourceDoc) addToActiveDocuments(sourceDoc);
                                  }}
                                  className="h-7 text-xs gap-1 flex-shrink-0"
                                >
                                  <Plus className="w-3 h-3" />
                                  Add to session
                                </Button>
                              );
                            })()}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-gray-500">
                            <span>{formatResourceId(doc.resource_id)}</span>
                            <span>•</span>
                            <Badge variant="outline" className="bg-gray-100 text-gray-700 border-gray-200 text-xs">
                              {doc.type}
                            </Badge>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => { e.stopPropagation(); const sourceDoc = getSourceDoc(doc); if (sourceDoc) openCollectionDialog(sourceDoc); }}
                        >
                          {isInAnyCollection(doc.resource_id || doc.id)
                            ? <BookmarkCheck className="w-4 h-4 text-blue-600" />
                            : <Bookmark className="w-4 h-4 text-gray-400" />}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white border border-gray-200 shadow-lg">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) addToActiveDocuments(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Add to session
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) openCollectionDialog(sourceDoc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              {isInAnyCollection(doc.resource_id || doc.id)
                                ? <><BookmarkCheck className="w-4 h-4 mr-2" /> Manage Collections</>
                                : <><FolderPlus className="w-4 h-4 mr-2" /> Add to Collection</>}
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && getDocumentUrl(sourceDoc) && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (sourceDoc) handleOpenInNewTab(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <ExternalLink className="w-4 h-4 mr-2" />
                                  Open in New Tab
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                onDocumentSelect(doc);
                              }}
                              className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {(() => {
                              const sourceDoc = getSourceDoc(doc);
                              return sourceDoc && sourceDoc.file_type?.toLowerCase() === 'upload' && (
                                <DropdownMenuItem
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownloadUpload(sourceDoc);
                                  }}
                                  className="text-gray-700 hover:bg-gray-100 cursor-pointer"
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  Download File
                                </DropdownMenuItem>
                              );
                            })()}
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                const sourceDoc = getSourceDoc(doc);
                                if (sourceDoc) handleDelete(sourceDoc);
                              }}
                              className="text-red-600 hover:bg-gray-100 cursor-pointer"
                              disabled={isDeleting}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>

                      {doc.summary && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{doc.summary}</p>
                      )}

                      <div className="flex items-center gap-6 text-xs text-gray-500">
                        {doc.onboardedDate && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Onboarded: {doc.onboardedDate}
                          </div>
                        )}
                        {doc.file_size && (
                          <div className="flex items-center gap-1">
                            <FileText className="w-3.5 h-3.5" />
                            {(doc.file_size / 1024).toFixed(1)} KB
                          </div>
                        )}
                        {doc.lastAccessed && doc.lastAccessed !== 'Never' && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            Accessed: {doc.lastAccessed}
                          </div>
                        )}
                      </div>

                      {/* Semantic Search Preview */}
                      {isFullTextSearch && doc.chunk_preview && (
                        <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-50 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-blue-600 text-white text-xs">MATCH</Badge>
                              {doc._sourceDoc?.num_chunks !== undefined && doc._sourceDoc?.total_chunks !== undefined && (
                                <Badge className="bg-green-600 text-white text-xs" title="Relevant sections / Total sections">
                                  {doc._sourceDoc.num_chunks}/{doc._sourceDoc.total_chunks}
                                </Badge>
                              )}
                              {doc._sourceDoc?.tf_score !== undefined && (
                                <Badge className="bg-purple-600 text-white text-xs" title="Relevance density (TF score)">
                                  {(doc._sourceDoc.tf_score * 100).toFixed(0)}%
                                </Badge>
                              )}
                              {doc._sourceDoc?.distance !== undefined && (
                                <Badge className="bg-gray-600 text-white text-xs" title="Best match quality">
                                  {(1 - doc._sourceDoc.distance).toFixed(3)}
                                </Badge>
                              )}
                            </div>
                            <div className="flex-1">
                              {doc.section_title && (
                                <div className="text-xs font-medium text-blue-900 mb-1 prose prose-sm max-w-none [&>p]:inline">
                                  <Markdown>{fixMarkdownFormatting(doc.section_title)}</Markdown>
                                </div>
                              )}
                              <div className="text-xs text-blue-900 leading-relaxed prose prose-sm max-w-none [&>p]:inline">
                                <Markdown>{fixMarkdownFormatting(doc.chunk_preview)}</Markdown>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
              })}
            </div>
          )}

          {/* Load More Button */}
          {!loading && hasMoreDocuments && filteredDocuments.length > 0 && (
            <div className="flex justify-center mt-6">
              <Button
                onClick={loadMoreDocuments}
                disabled={loadingMore}
                variant="outline"
                className="gap-2"
              >
                {loadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Load More
                    <ChevronDown className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          )}

          {!loading && filteredDocuments.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No documents found</p>
              <p className="text-sm text-gray-400 mt-1">Try adjusting your search or filters</p>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Collection Dialog */}
      <Dialog open={showCollectionDialog} onOpenChange={setShowCollectionDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Collections</DialogTitle>
            <DialogDescription>
              Add or remove this document from your collections.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-60 overflow-y-auto">
            {collections.map(col => {
              const isIn = collectionDialogDoc ? col.resource_ids.includes(collectionDialogDoc.resource_id) : false;
              return (
                <div
                  key={col.collection_id}
                  className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer hover:bg-gray-50 ${isIn ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}
                  onClick={() => collectionDialogDoc && toggleDocInCollection(col.collection_id, collectionDialogDoc.resource_id)}
                >
                  <span className="text-sm font-medium">{col.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">{col.resource_ids.length} docs</span>
                    {isIn ? <BookmarkCheck className="w-4 h-4 text-blue-600" /> : <Bookmark className="w-4 h-4 text-gray-300" />}
                  </div>
                </div>
              );
            })}
            {collections.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No collections yet. Create one below.</p>
            )}
          </div>
          <div className="flex items-center gap-2 pt-2 border-t">
            <Input
              placeholder="New collection name..."
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newCollectionName.trim()) {
                  createCollection(newCollectionName.trim()).then(id => {
                    if (collectionDialogDoc) toggleDocInCollection(id, collectionDialogDoc.resource_id);
                  });
                  setNewCollectionName('');
                }
              }}
            />
            <Button
              size="sm"
              disabled={!newCollectionName.trim()}
              onClick={() => {
                if (newCollectionName.trim()) {
                  createCollection(newCollectionName.trim()).then(id => {
                    if (collectionDialogDoc) toggleDocInCollection(id, collectionDialogDoc.resource_id);
                  });
                  setNewCollectionName('');
                }
              }}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCollectionDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <span className="font-semibold">{documentToDelete?.file_name}</span>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collections Manager Modal */}
      <CollectionsManagerModal open={showCollectionsManager} onOpenChange={setShowCollectionsManager} />

      {/* Multi-Doc Q&A Panel */}
      <MultiDocQAPanel
        open={showMultiDocQA}
        onOpenChange={setShowMultiDocQA}
        sessionDocuments={sessionDocuments}
      />
    </div>
  );
}
